import { useEffect, useState } from "react";

import { isApolloError } from "@apollo/client";
import { Box, Center, Flex } from "@chakra-ui/react";
import { groupBy } from "lodash";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { RESTOCKING_PROGRESSED_METHOD } from "analytics/events";
import { EppoFeatureFlags } from "app/types/flags";
import { routes } from "config/routes";
import { EndDroppingProcessButton } from "flows/Inbound/components/EndDroppingProcessButton";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";
import { ProductInformation } from "ui/ProductInformation/ProductInformation";
import { Shelf } from "ui/Shelf/Shelf";
import { Spinner } from "ui/Spinner/Spinner";
import { Status } from "ui/Status/Status";
import { TitleXS } from "ui/Typography/Typography";
import { createRestockingItemComparator, getShelfDetail } from "utils/item";
import { isNullOrUndefined } from "utils/tsHelpers";

import { RestockingListCard } from "../../components/RestockingListCard";
import { PublicRestockingItem } from "../../models/restockingItem/types";
import {
  usePrivateRestockingList,
  usePublicRestockingList,
} from "../../services/restockingService/service";
import { useRestockingServiceStore } from "../../stores/restockingServiceStore";
import { applyRestockingFilters } from "../../utils/applyRestockingFilters";

const CREATE_PRIVATE_RESTOCKING_LIST_ERROR_TOAST_ID = "create_private_restocking_list_error_toast";
const CREATE_PRIVATE_RESTOCKING_LIST_SUCCESS_TOAST_ID =
  "create_private_restocking_list_success_toast";

function NewProductList({
  items,
  selectedRestockingItems,
  setSelectedRestockingItems,
}: {
  items: PublicRestockingItem[];
  selectedRestockingItems: string[];
  setSelectedRestockingItems: (selectedRestockingItems: string[]) => void;
}) {
  const onClickProductCard = (selectedItem: string) => () => {
    setSelectedRestockingItems(
      selectedRestockingItems.includes(selectedItem)
        ? selectedRestockingItems.filter((restockingItem) => restockingItem !== selectedItem)
        : [...selectedRestockingItems, selectedItem],
    );
  };
  return items.map((item) => (
    <Flex
      onClick={onClickProductCard(item.sku)}
      pos="relative"
      key={item.sku}
      data-testid="productCard"
    >
      <Box
        pos="absolute"
        bg="grey.800"
        top="0"
        left="0"
        height="100%"
        width="100%"
        transition="opacity 50ms linear"
        borderRadius="sm"
        zIndex="10"
        opacity={
          selectedRestockingItems.some((restockingItem) => restockingItem === item.sku) ? 0.1 : 0
        }
      />

      <RestockingListCard
        isRestocked={false}
        name={item?.product?.name ?? ""}
        imageUrl={item?.product?.imageUrl ?? null}
        stockOnShelf={item?.product?.stockOnShelf ?? 0}
        shelf={item?.product?.shelf ?? null}
        numberOfShelfFacings={item?.product?.numberOfShelfFacings ?? null}
        numberOfShelfStackings={item?.product?.numberOfShelfStackings ?? null}
        isShelvedInHandlingUnits={item?.product?.isShelvedInHandlingUnits ?? null}
        priorityTier={item?.priorityTier ?? null}
      />
    </Flex>
  ));
}

function ProductList({
  shelf,
  items,
  selectedRestockingItems,
  setSelectedRestockingItems,
}: {
  shelf: string;
  items: PublicRestockingItem[];
  selectedRestockingItems: string[];
  setSelectedRestockingItems: (selectedRestockingItems: string[]) => void;
}) {
  const { shelfNumber: number, shelfLetter: letter } = getShelfDetail(shelf);
  const displayedShelf =
    shelf === "N/A" ? (
      <Status bg="grey.200" textColor="black" gridColumnStart="3" label="N/A" size="small" />
    ) : (
      <Shelf letter={letter} number={number} variant="small" />
    );
  const onClickProductCard = (selectedItem: string) => () => {
    setSelectedRestockingItems(
      selectedRestockingItems.includes(selectedItem)
        ? selectedRestockingItems.filter((restockingItem) => restockingItem !== selectedItem)
        : [...selectedRestockingItems, selectedItem],
    );
  };
  return (
    <>
      <Flex px={4} py={2} alignItems="center" bg="grey.150">
        {displayedShelf}
      </Flex>
      {items.map((item) => (
        <Flex
          onClick={onClickProductCard(item.sku)}
          alignItems="center"
          py="s200"
          px="s100"
          pos="relative"
          bg="white"
          key={item.sku}
          data-testid="productCard"
        >
          <Box
            pos="absolute"
            bg="grey.800"
            top="0"
            left="0"
            height="100%"
            width="100%"
            transition="opacity 50ms linear"
            opacity={
              selectedRestockingItems.some((restockingItem) => restockingItem === item.sku)
                ? 0.1
                : 0
            }
          />
          <ProductInformation
            name={item.product?.name || ""}
            imageUrl={item.product?.imageUrl}
            sku={item.sku}
            variantSize="s"
            bg="none"
            stock={item.product?.stockOnShelf ?? "N/A"}
            stockStatus="validated"
          />
        </Flex>
      ))}
    </>
  );
}

export function RestockingPublicList() {
  const intl = useIntl();
  const { showToastUI } = useCustomToast();

  const navigate = useNavigate();

  const { isFeatureEnabled: isRestockingV2Enabled } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.ENABLE_RESTOCKING_V2,
  );
  const { restockingItems, isLoadingPublicRestockingList } = usePublicRestockingList();

  const { categoryFilters, shelfFilters, priorityFilters, sortOrder, sortBy } =
    useRestockingServiceStore(
      useShallow((state) => ({
        categoryFilters: state.categoryFilters,
        shelfFilters: state.shelfFilters,
        priorityFilters: state.priorityFilters,
        sortOrder: state.sortOrder,
        sortBy: state.sortBy,
      })),
    );

  const [selectedRestockingItems, setSelectedRestockingItems] = useState<string[]>([]);
  const [isLoadingItemsToPrivateList, setIsLoadingItemsToPrivateList] = useState(false);

  const hasFiltersApplied =
    categoryFilters.length > 0 || shelfFilters.length > 0 || priorityFilters.length > 0;

  const filteredRestockingItems = restockingItems
    ? applyRestockingFilters(restockingItems, categoryFilters, shelfFilters, priorityFilters)
    : null;

  useEffect(() => {
    if (selectedRestockingItems.length === 0) return;
    const visibleSkus = new Set(filteredRestockingItems?.map((item) => item.sku) ?? []);
    setSelectedRestockingItems((prev) => prev.filter((sku) => visibleSkus.has(sku)));
  }, [filteredRestockingItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = groupBy(filteredRestockingItems, (item) => {
    const shelf = item.product?.shelf;
    return isNullOrUndefined(shelf) ? "N/A" : shelf;
  });
  const comparePublicItems = createRestockingItemComparator(sortBy, sortOrder);

  const sortedAndGroupedRestockingItems = Object.entries(grouped)
    .map(
      ([shelf, items]) =>
        [shelf, [...items].sort(comparePublicItems)] as [string, PublicRestockingItem[]],
    )
    .sort(([, itemsA], [, itemsB]) => comparePublicItems(itemsA[0] ?? {}, itemsB[0] ?? {}));

  const sortedAndFilteredRestockingItems = [...(filteredRestockingItems ?? [])].sort(
    comparePublicItems,
  );

  const { addItemsToPrivateRestockingList } = usePrivateRestockingList({ loadOnInit: false });

  const loadItemsToPrivateList = async (skus: string[]) => {
    try {
      setIsLoadingItemsToPrivateList(true);
      await addItemsToPrivateRestockingList(skus, RESTOCKING_PROGRESSED_METHOD.PRE_LOADED_LIST);
      navigate(routes.inventory.restockingList.root);
      showToastUI({
        id: CREATE_PRIVATE_RESTOCKING_LIST_SUCCESS_TOAST_ID,
        status: "success",
        containerStyle: {
          width: "100%",
          bottom: "150px",
          position: "absolute",
          paddingLeft: "s100",
          paddingRight: "s100",
        },
        title: "components.picking.restocking.public-restocking-items-success-title",
      });
    } catch (error: any) {
      const isLimitExceeded =
        error &&
        isApolloError(error) &&
        error.graphQLErrors?.[0]?.extensions?.code ===
          "PRIVATE_RESTOCKING_LIST_LIMIT_EXCEEDED_ERROR";

      if (isLimitExceeded) {
        showToastUI({
          id: "create-private-restocking-error",
          title: "create-private-restocking-error.item-limit-exceeded",
        });
        setIsLoadingItemsToPrivateList(false);
        navigate(routes.inventory.restockingList.root);
        return;
      }
      showToastUI({
        id: CREATE_PRIVATE_RESTOCKING_LIST_ERROR_TOAST_ID,
        title: "flows.inventory.restocking-list.add-items-to-private-list-error",
      });
      setIsLoadingItemsToPrivateList(false);
    }
  };

  const onClickAddItemToList = () => {
    loadItemsToPrivateList(selectedRestockingItems);
  };

  const onClickAddAllItemsToList = () => {
    if (filteredRestockingItems) {
      const allSkus = filteredRestockingItems.map((item) => item.sku);
      loadItemsToPrivateList(allSkus);
    }
  };

  const getMainCTALabel = () => {
    if (selectedRestockingItems.length > 0) {
      return "flows.inventory.restocking-list.pages.list-of-items.button.add-items-to-the-list-selected";
    } else if (hasFiltersApplied) {
      return "flows.inventory.restocking-list.pages.list-of-items.button.add-items-to-the-list-filtered-all";
    } else {
      return "flows.inventory.restocking-list.pages.list-of-items.button.add-items-to-the-list-all";
    }
  };

  if (isLoadingPublicRestockingList)
    return (
      <Center h="full">
        <Spinner />
      </Center>
    );

  return (
    <Flex
      direction="column"
      w="100%"
      flex={1}
      gap={2}
      bg="grey.150"
      p="s100"
      pb="calc(12px + 48px + 12px)"
    >
      {filteredRestockingItems === null ? (
        <TitleXS color="grey.800" fontWeight={700} noOfLines={2}>
          {intl.formatMessage({
            id: "flows.inventory.restocking-list.pages.list-of-items.paragraph.something-went-wrong",
          })}
        </TitleXS>
      ) : (
        <>
          {isRestockingV2Enabled ? (
            <NewProductList
              items={sortedAndFilteredRestockingItems}
              selectedRestockingItems={selectedRestockingItems}
              setSelectedRestockingItems={setSelectedRestockingItems}
            />
          ) : (
            sortedAndGroupedRestockingItems.map(([shelf, items]) => (
              <ProductList
                key={shelf}
                shelf={shelf}
                items={items}
                selectedRestockingItems={selectedRestockingItems}
                setSelectedRestockingItems={setSelectedRestockingItems}
              />
            ))
          )}
          <EndDroppingProcessButton
            zIndex={15}
            shouldShowButton={(filteredRestockingItems?.length ?? 0) > 0}
            isLoading={isLoadingItemsToPrivateList}
            onClickButton={
              selectedRestockingItems.length > 0 ? onClickAddItemToList : onClickAddAllItemsToList
            }
            labelMessageId={getMainCTALabel()}
            values={
              selectedRestockingItems.length > 0
                ? { amount: selectedRestockingItems.length }
                : undefined
            }
            buttonTestId="add-items-to-restocking-list-button"
            bottom="0"
          />
        </>
      )}
    </Flex>
  );
}
